import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * 用于延迟的钩子
 * 内部会卸载定时器
 * 新建定时器时会自动取消上一个定时器
 */
const useDelay = () => {
  // 存储定时器id
  const timerRef = useRef<any>(null);
  // 存储当前挂起的Promise的resolve函数
  const pendingResolveRef = useRef<((ok: boolean) => void) | null>(null);

  /**
   * 结束当前挂起的Promise，并传递结果
   */
  const resolvePending = useCallback((ok: boolean) => {
    // 找到当前存储的挂起Promise的resolve函数
    const resolve = pendingResolveRef.current;
    // 如果有则调用它，并传递结果
    if (resolve) {
      pendingResolveRef.current = null;
      try {
        resolve(ok);
      } catch (_) {}
    }
  }, []);

  /**
   * 清理定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      try {
        clearTimeout(timerRef.current);
      } catch (_) {}
      timerRef.current = null;
    }
  }, []);

  /**
   * 组件卸载时，清理定时器
   */
  useEffect(() => {
    return () => {
      // 组件卸载时清理定时器
      clearTimer();

      // 组件卸载时若仍有Promise挂起，主动结束它
      resolvePending(false);
    };
  }, [clearTimer, resolvePending]);

  /**
   * 取消定时器
   */
  const cancel = useCallback(() => {
    // 清除定时器
    clearTimer();

    // 取消时若仍有Promise挂起，主动结束它
    resolvePending(false);
  }, [clearTimer, resolvePending]);

  /**
   * 返回一个 Promise，在指定毫秒数后才会resolve
   * 调用前会先取消上一次的定时器
   */
  const delay = useCallback(
    (ms: number) => {
      return new Promise<boolean>((resolve) => {
        try {
          cancel();
          pendingResolveRef.current = resolve;
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            pendingResolveRef.current = null;
            resolve(true);
          }, ms);
        } catch (_) {
          pendingResolveRef.current = null;
          resolve(true);
        }
      });
    },
    [cancel]
  );

  return useMemo(() => ({ delay, cancel }), [delay, cancel]);
};

export default useDelay;
